import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { SummaryRow } from '@cosmos/shared';
import { ColumnDefinition } from '../../../data/data-table/column-definition.model';
import { SummaryConfigContext, SummaryTableModel, SummaryTypeUi } from './summary-type-ui.model';

const col = (name: string, label: string, type?: string, rightBorder = false): ColumnDefinition => ({
  name, label, visible: true, sortable: true, draggable: false, type, rightBorder,
});

export const maturitySummaryUi: SummaryTypeUi = {
  configFields(ctx: SummaryConfigContext): FormlyFieldConfig[] {
    const t = (k: string) => ctx.translate.instant(k);
    const fields: FormlyFieldConfig[] = [
      {
        key: 'strengthCheckpoints', type: 'chips-number', wrappers: ['default-wrapper'],
        props: { label: t('summarize-measurements.config.strengthCheckpoints'), suffix: ' MPa' },
      },
      {
        key: 'daysCheckpoints', type: 'chips-number', wrappers: ['default-wrapper'],
        props: { label: t('summarize-measurements.config.daysCheckpoints'), suffix: ' d' },
      },
      {
        key: 'strengthCheckpointExtras', type: 'multiselect', wrappers: ['default-wrapper'],
        props: {
          label: t('summarize-measurements.config.strengthCheckpointExtras'),
          options: [
            { value: 'maxTempAtReach', label: t('summarize-measurements.config.extras.maxTempAtReach') },
          ],
        },
      },
    ];
    if (ctx.advancedOptions) {
      fields.push({
        key: 'includeSpecComparison', type: 'checkbox', wrappers: ['default-wrapper'],
        props: { label: t('summarize-measurements.config.includeSpecComparison') },
      });
    }
    return fields;
  },

  validate(model): boolean {
    const s = model?.['strengthCheckpoints'] as number[] | undefined;
    const d = model?.['daysCheckpoints'] as number[] | undefined;
    return !!(s?.length || d?.length);
  },

  buildTable(rows: SummaryRow[], config: Record<string, unknown>, translate: TranslateService): SummaryTableModel {
    const strengthCps = (config['strengthCheckpoints'] as number[]) ?? [];
    const dayCps = (config['daysCheckpoints'] as number[]) ?? [];
    const extras = (config['strengthCheckpointExtras'] as string[]) ?? [];
    const includeSpec = !!config['includeSpecComparison'];
    const t = (k: string, p?: object) => translate.instant(k, p);

    const columns: ColumnDefinition[] = [
      col('name', t('table.labels.name')),
      col('stageName', t('table.labels.stage')),
      col('startTime', t('table.labels.startDate'), 'date', true),
    ];

    for (const cp of strengthCps) {
      const hasExtra = extras.includes('maxTempAtReach');
      columns.push(
        col(`strengthCheckpoints.${cp}.strength`, t('table.labels.strengthAtStrength', { cp }), 'number'),
        col(`strengthCheckpoints.${cp}.timeReached`, t('table.labels.timeReachedAtStrength', { cp }), 'date'),
        col(`strengthCheckpoints.${cp}.timeDiffHours`, t('table.labels.timeDiffHoursAtStrength', { cp }), 'number', !hasExtra),
      );
      if (hasExtra) {
        columns.push(col(`strengthCheckpoints.${cp}.maxTempAtReach`, t('table.labels.maxTempAtReachAtStrength', { cp }), 'number', true));
      }
    }
    for (const cp of dayCps) {
      columns.push(
        col(`daysCheckpoints.${cp}.strength`, t('table.labels.strengthAtDay', { cp }), 'number'),
        col(`daysCheckpoints.${cp}.timeReached`, t('table.labels.timeReachedAtDay', { cp }), 'date', true),
      );
    }
    if (includeSpec) {
      const specs = [...new Set(rows.flatMap(r => (r.specComparison ?? []).map(s => s.spec)))].sort((a, b) => a - b);
      for (const spec of specs) {
        columns.push({
          name: `specComparison.${spec}.met`, label: t('table.labels.specMet', { spec }),
          visible: true, sortable: true, draggable: false, type: 'boolean',
        });
      }
    }

    const data = rows.map(row => {
      const out: Record<string, unknown> = { name: row.name, stageName: row.stageName ?? '-', startTime: row.startTime };
      const sMap: Record<string, unknown> = {};
      for (const c of row.strengthCheckpoints ?? []) sMap[c.checkpoint] = c;
      const dMap: Record<string, unknown> = {};
      for (const c of row.daysCheckpoints ?? []) dMap[c.checkpoint] = c;
      out['strengthCheckpoints'] = sMap;
      out['daysCheckpoints'] = dMap;
      if (includeSpec) {
        const spMap: Record<string, unknown> = {};
        for (const s of row.specComparison ?? []) spMap[s.spec] = s;
        out['specComparison'] = spMap;
      }
      return out;
    });

    return { columns, data };
  },
};
